/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import { Coordinates, Coordinates$inboundSchema } from "./coordinates.js";
import {
  LinearGaugeConig,
  LinearGaugeConig$inboundSchema,
} from "./lineargaugeconig.js";
import {
  LinearSetpointGaugeConfg,
  LinearSetpointGaugeConfg$inboundSchema,
} from "./linearsetpointgaugeconfg.js";
import { Ref, Ref$inboundSchema } from "./ref.js";

/**
 * The orientation of the arrow on the gauge. 'Right' means the arrow is located on the right, pointing left.
 */
export const ArrowOrientation = {
  Left: "left",
  Right: "right",
  Top: "top",
  Bottom: "bottom",
} as const;
/**
 * The orientation of the arrow on the gauge. 'Right' means the arrow is located on the right, pointing left.
 */
export type ArrowOrientation = OpenEnum<typeof ArrowOrientation>;

/**
 * Defines an animatic gauge that is overlaid on top of an animatic, its states, and how it should be configured for each state.
 */
export type AnimaticGaugeConfig = {
  /**
   * The orientation of the arrow on the gauge. 'Right' means the arrow is located on the right, pointing left.
   */
  arrowOrientation?: ArrowOrientation | undefined;
  /**
   * The coordinates of an object on the animatic.
   */
  coordinates?: Coordinates | undefined;
  /**
   * The description of the gauge. This is used to provide more information about the gauge.
   */
  description?: string | undefined;
  /**
   * The label of the gauge. This is used to identify the gauge in the client, ideally it should be unique.
   */
  label?: string | undefined;
  /**
   * Configuration that is specific to a Linear Gauge.
   */
  linear?: LinearGaugeConig | undefined;
  /**
   * Defines a linear setpoint gauge that is overlaid on top of an animatic.
   */
  linearSetpoint?: LinearSetpointGaugeConfg | undefined;
  /**
   * A reference to a reading or setting.
   */
  ref?: Ref | undefined;
  /**
   * Whether to show the marker on the gauge.
   */
  showArrow: boolean;
};

/** @internal */
export const ArrowOrientation$inboundSchema: z.ZodType<
  ArrowOrientation,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(ArrowOrientation);

/** @internal */
export const AnimaticGaugeConfig$inboundSchema: z.ZodType<
  AnimaticGaugeConfig,
  z.ZodTypeDef,
  unknown
> = z.object({
  arrow_orientation: types.optional(ArrowOrientation$inboundSchema),
  coordinates: types.optional(Coordinates$inboundSchema),
  description: types.optional(types.string()),
  label: types.optional(types.string()),
  linear: types.optional(LinearGaugeConig$inboundSchema),
  linear_setpoint: types.optional(LinearSetpointGaugeConfg$inboundSchema),
  ref: types.optional(Ref$inboundSchema),
  show_arrow: types.boolean().default(false),
}).transform((v) => {
  return remap$(v, {
    "arrow_orientation": "arrowOrientation",
    "linear_setpoint": "linearSetpoint",
    "show_arrow": "showArrow",
  });
});

export function animaticGaugeConfigFromJSON(
  jsonString: string,
): SafeParseResult<AnimaticGaugeConfig, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AnimaticGaugeConfig$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AnimaticGaugeConfig' from JSON`,
  );
}
